import React, { useMemo } from 'react';
import { bandChipClass, admitRatePercent, UNSCORED_LABEL } from '../../utils/fitBands';
import { balanceVerdict, lengthVerdict } from '../../utils/listBalance';

// ============================================================================
// IN-THREAD COLLEGE-LIST PANEL (#1198)
//
// Founder 2026-08-03: asked "is my list balanced?", the concierge read the
// list AND the candidates and then answered from conversation memory — two
// Targets and a Safety described as Reaches, two schools that were never on
// the list at all, and "a good mix" against an actual 0 reach / 2 target /
// 2 safety. Bands are recomputed by the fit analysis when a school is added,
// so anything remembered from an earlier candidates read is stale.
//
// So the answer is RENDERED, not narrated: rows and counts derived by the
// server from that turn's own get_college_list result.
//
// Rules:
//   - this panel offers NO action. Every school here is already on the list;
//     an "add" affordance would be nonsense. It is the answer, not a choice.
//   - the counts come from the server, and fold SUPER_REACH into Reach the
//     same way the Launchpad does, so the two can never disagree
//   - the verdict is the Launchpad's own tested `balanceVerdict` — one
//     definition of "balanced", not a second opinion in the chat — and,
//     since #3041, its `lengthVerdict` over the same total (a 1-1-1 list is
//     balanced in shape and still too short)
//   - a missing admit rate reads as unknown, never as 0%
//   - a school with no computed fit shows no band chip and is counted apart
// ============================================================================

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

const normalizeRows = (raw) => {
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const s of raw) {
        const name = str(s?.name);
        if (!name) continue;
        out.push({
            id: str(s.university_id) || name,
            name,
            band: str(s.band),
            estimated: s.estimated === true,
            admit: admitRatePercent(s.acceptance_rate),
        });
    }
    return out;
};

const int = (v) => (Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0);

export default function ListPanel({ panel }) {
    const rows = useMemo(() => normalizeRows(panel?.schools), [panel]);
    if (!panel || typeof panel !== 'object') return null;

    const counts = panel.counts || {};
    const reach = int(counts.Reach);
    const target = int(counts.Target);
    const safety = int(counts.Safety);
    const unknown = int(counts.unknown);
    const total = Number.isFinite(panel.total) ? Math.floor(panel.total)
        : reach + target + safety + unknown;
    const verdict = balanceVerdict({ reach, target, safety });
    const length = lengthVerdict(total, { stillBuilding: panel.still_building === true });

    return (
        <div data-testid="concierge-list-panel"
             className="mb-2 rounded-xl border border-[#D6E8D5] bg-[#FAFCFA] p-2.5">
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#9A968C]">
                    Your list · {total} {total === 1 ? 'school' : 'schools'}
                </span>
            </div>

            {/* The counts, straight from the server's read of the list. This is
                the number a student decides with, so it is never prose. */}
            <p className="mt-1 font-mono text-[11px] tabular-nums text-[#2C2C2C]"
               data-testid="concierge-list-counts">
                {reach} reach · {target} target · {safety} safety
                {unknown > 0 ? ` · ${unknown} ${UNSCORED_LABEL.toLowerCase()}` : ''}
            </p>
            <p className="mt-0.5 text-[11px] text-[#6B6B6B]" data-testid="concierge-list-verdict">
                <span className="font-semibold">{verdict.headline}.</span> {verdict.detail}
            </p>
            {length && (
                <p className="mt-0.5 text-[11px] text-[#6B6B6B]" data-testid="concierge-list-length">
                    <span className="font-semibold">{length.headline}.</span> {length.detail}
                </p>
            )}

            {rows.length > 0 && (
                <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto pr-0.5">
                    {rows.map((row) => (
                        <li key={row.id} data-testid="concierge-list-school"
                            className="rounded-lg border border-[#EFEDE7] bg-white px-2.5 py-1.5">
                            <span className="block truncate text-[13px] font-semibold text-[#2C2C2C]">
                                {row.name}
                            </span>
                            <span className="sr-only">
                                {row.band ? `${row.band}${row.estimated ? ', estimated from its admit rate' : ''}.`
                                    : `${UNSCORED_LABEL}.`}
                                {` Acceptance rate ${row.admit != null ? `${row.admit}%` : 'not available'}.`}
                            </span>
                            <span aria-hidden="true" className="mt-0.5 flex flex-wrap items-center gap-1">
                                {row.band ? (
                                    <span data-testid="concierge-list-band"
                                          className={`rounded-full border px-1.5 py-px text-[9.5px] font-semibold
                                              ${bandChipClass(row.band) || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                        {row.band}{row.estimated ? ' (est.)' : ''}
                                    </span>
                                ) : (
                                    <span data-testid="concierge-list-unanalyzed"
                                          className="rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px
                                              text-[9.5px] font-semibold text-gray-600">
                                        {UNSCORED_LABEL}
                                    </span>
                                )}
                                <span className="font-mono text-[10px] tabular-nums text-[#9A968C]">
                                    {row.admit != null ? `${row.admit}%` : '—'} admit
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {total > rows.length && (
                <p className="mt-1.5 text-[10px] text-[#9A968C]" data-testid="concierge-list-truncated">
                    Showing {rows.length} of {total} — the rest are on your Launchpad.
                </p>
            )}
        </div>
    );
}
